import { Injectable, signal, inject } from '@angular/core';
import { NotificationService } from './notification.service';

export type AdminToastVariant = 'approved' | 'denied' | 'info' | 'success' | 'warning';

export interface AdminToast {
  id: number;
  message: string;
  variant: AdminToastVariant;
  dot: string;
  canUndo: boolean;
  onUndo?: () => void;
}

export interface AdminToastOptions {
  variant?: AdminToastVariant;
  canUndo?: boolean;
  onUndo?: () => void;
  durationMs?: number;
}

const DEFAULT_DURATION_MS = 6000;

const DOT_BY_VARIANT: Record<AdminToastVariant, string> = {
  approved: '#34D399',
  success: '#34D399',
  denied: '#F59E0B',
  warning: '#F59E0B',
  info: '#00B2F7',
};

@Injectable({ providedIn: 'root' })
export class AdminToastService {
  private readonly notifications = inject(NotificationService);
  private nextId = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;

  readonly currentToast = signal<AdminToast | null>(null);

  show(message: string, options: AdminToastOptions = {}): AdminToast {
    const variant: AdminToastVariant = options.variant ?? 'info';
    const toast: AdminToast = {
      id: this.nextId++,
      message,
      variant,
      dot: DOT_BY_VARIANT[variant],
      canUndo: options.canUndo === true && typeof options.onUndo === 'function',
      onUndo: options.onUndo,
    };
    this.clearTimer();
    this.currentToast.set(toast);
    const duration = options.durationMs ?? DEFAULT_DURATION_MS;
    if (duration > 0) {
      this.timer = setTimeout(() => this.dismiss(), duration);
    }
    return toast;
  }

  showApproved(message: string, options: Omit<AdminToastOptions, 'variant'> = {}): AdminToast {
    return this.show(message, { ...options, variant: 'approved' });
  }

  showDenied(message: string, options: Omit<AdminToastOptions, 'variant'> = {}): AdminToast {
    return this.show(message, { ...options, variant: 'denied' });
  }

  showInviteUser(email: string): AdminToast {
    return this.show(`Invite sent to ${email} — Okta will email a provisioning link (demo only).`, {
      variant: 'info',
    });
  }

  dismiss(): void {
    this.clearTimer();
    this.currentToast.set(null);
  }

  triggerUndo(): void {
    const toast = this.currentToast();
    if (!toast || !toast.canUndo) {
      return;
    }
    try {
      toast.onUndo?.();
    } finally {
      this.dismiss();
    }
  }

  showSuccess(message: string): void {
    this.show(message, { variant: 'success' });
  }

  showError(message: string): void {
    this.notifications.showError(message);
  }

  showInfo(message: string): void {
    this.show(message, { variant: 'info' });
  }

  showWarning(message: string): void {
    this.show(message, { variant: 'warning' });
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
